import type { OfficeLayoutV1 } from "spatial-contracts";

/**
 * Cozy HQ — The default HuddleZen office layout.
 * 
 * Layout:
 *   ┌───────────────────────────────────────────────────────┐
 *   │                                                       │
 *   │    ┌──────────┐    ┌──────────┐    ┌──────────┐      │
 *   │    │  Focus    │    │  Focus   │    │ Meeting  │      │
 *   │    │  Room A   │    │  Room B  │    │  Room    │      │
 *   │    └────  ─────┘    └────  ────┘    └────  ────┘      │
 *   │                                                       │
 *   │              ◆ Open Workspace ◆                       │
 *   │                                                       │
 *   │    ┌──────────────────────┐    ┌─────────────┐       │
 *   │    │    Lounge            │    │   Kitchen    │       │
 *   │    │                     │    │              │       │
 *   │    └─────────  ──────────┘    └─────  ──────┘       │
 *   │                                                       │
 *   └───────────────────────────────────────────────────────┘
 *
 *  Grid: 30 x 24 tiles @ 48px = 1440 x 1152 world pixels
 */
export const cozyHqLayout: OfficeLayoutV1 = {
  schemaVersion: "v1",
  layoutRevision: 2,
  width: 30,
  height: 24,
  tileSize: 48,
  themeId: "cozy-classic",
  assetPackIds: ["basic-office"],
  spawnPoint: { x: 15, y: 12 },
  walls: [
    // ── Outer perimeter ──
    { id: "wall-n", startX: 0, startY: 0, endX: 29, endY: 0, isDoor: false },
    { id: "wall-s", startX: 0, startY: 23, endX: 29, endY: 23, isDoor: false },
    { id: "wall-w", startX: 0, startY: 0, endX: 0, endY: 23, isDoor: false },
    { id: "wall-e", startX: 29, startY: 0, endX: 29, endY: 23, isDoor: false },

    // ── Focus Room A (top-left: cols 2-7, rows 2-6) ──
    { id: "fr-a-n", startX: 2, startY: 2, endX: 7, endY: 2, isDoor: false },
    { id: "fr-a-w", startX: 2, startY: 2, endX: 2, endY: 6, isDoor: false },
    { id: "fr-a-e", startX: 7, startY: 2, endX: 7, endY: 6, isDoor: false },
    { id: "fr-a-s1", startX: 2, startY: 6, endX: 4, endY: 6, isDoor: false },
    { id: "fr-a-door", startX: 5, startY: 6, endX: 5, endY: 6, isDoor: true },
    { id: "fr-a-s2", startX: 6, startY: 6, endX: 7, endY: 6, isDoor: false },

    // ── Focus Room B (top-center: cols 10-15, rows 2-6) ──
    { id: "fr-b-n", startX: 10, startY: 2, endX: 15, endY: 2, isDoor: false },
    { id: "fr-b-w", startX: 10, startY: 2, endX: 10, endY: 6, isDoor: false },
    { id: "fr-b-e", startX: 15, startY: 2, endX: 15, endY: 6, isDoor: false },
    { id: "fr-b-s1", startX: 10, startY: 6, endX: 12, endY: 6, isDoor: false },
    { id: "fr-b-door", startX: 13, startY: 6, endX: 13, endY: 6, isDoor: true },
    { id: "fr-b-s2", startX: 14, startY: 6, endX: 15, endY: 6, isDoor: false },

    // ── Meeting Room (top-right: cols 18-25, rows 2-6) ──
    { id: "mr-n", startX: 18, startY: 2, endX: 25, endY: 2, isDoor: false },
    { id: "mr-w", startX: 18, startY: 2, endX: 18, endY: 6, isDoor: false },
    { id: "mr-e", startX: 25, startY: 2, endX: 25, endY: 6, isDoor: false },
    { id: "mr-s1", startX: 18, startY: 6, endX: 20, endY: 6, isDoor: false },
    { id: "mr-door", startX: 21, startY: 6, endX: 21, endY: 6, isDoor: true },
    { id: "mr-s2", startX: 22, startY: 6, endX: 25, endY: 6, isDoor: false },

    // ── Lounge (bottom-left: cols 2-15, rows 16-21) ──
    { id: "lg-n1", startX: 2, startY: 16, endX: 8, endY: 16, isDoor: false },
    { id: "lg-door", startX: 9, startY: 16, endX: 9, endY: 16, isDoor: true },
    { id: "lg-n2", startX: 10, startY: 16, endX: 15, endY: 16, isDoor: false },
    { id: "lg-w", startX: 2, startY: 16, endX: 2, endY: 21, isDoor: false },
    { id: "lg-e", startX: 15, startY: 16, endX: 15, endY: 21, isDoor: false },
    { id: "lg-s", startX: 2, startY: 21, endX: 15, endY: 21, isDoor: false },

    // ── Kitchen (bottom-right: cols 18-26, rows 16-21) ──
    { id: "kt-n1", startX: 18, startY: 16, endX: 21, endY: 16, isDoor: false },
    { id: "kt-door", startX: 22, startY: 16, endX: 22, endY: 16, isDoor: true },
    { id: "kt-n2", startX: 23, startY: 16, endX: 26, endY: 16, isDoor: false },
    { id: "kt-w", startX: 18, startY: 16, endX: 18, endY: 21, isDoor: false },
    { id: "kt-e", startX: 26, startY: 16, endX: 26, endY: 21, isDoor: false },
    { id: "kt-s", startX: 18, startY: 21, endX: 26, endY: 21, isDoor: false },
  ],
  furniture: [
    // Focus Room A desks
    { id: "desk-a1", itemType: "desk", x: 3, y: 3, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 2, height: 1 } },
    { id: "desk-a2", itemType: "desk", x: 3, y: 5, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 2, height: 1 } },
    // Focus Room B desks
    { id: "desk-b1", itemType: "desk", x: 11, y: 3, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 2, height: 1 } },
    { id: "desk-b2", itemType: "desk", x: 11, y: 5, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 2, height: 1 } },
    // Meeting Room table
    { id: "mtg-table", itemType: "desk", x: 20, y: 3, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 4, height: 2 } },
    // Open workspace desks (center)
    { id: "desk-open-1", itemType: "desk", x: 5, y: 10, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 3, height: 1 } },
    { id: "desk-open-2", itemType: "desk", x: 5, y: 12, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 3, height: 1 } },
    { id: "desk-open-3", itemType: "desk", x: 12, y: 10, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 3, height: 1 } },
    { id: "desk-open-4", itemType: "desk", x: 12, y: 12, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 3, height: 1 } },
    { id: "desk-open-5", itemType: "desk", x: 20, y: 10, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 3, height: 1 } },
    { id: "desk-open-6", itemType: "desk", x: 20, y: 12, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 3, height: 1 } },
    // Lounge sofas
    { id: "sofa-1", itemType: "desk", x: 4, y: 18, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 3, height: 1 } },
    { id: "sofa-2", itemType: "desk", x: 10, y: 18, rotation: 0, collisionFootprint: { offsetX: 0, offsetY: 0, width: 3, height: 1 } },
    // Plants
    { id: "plant-1", itemType: "plant", x: 1, y: 1, rotation: 0, collisionFootprint: { offsetX: 0.1, offsetY: 0.1, width: 0.8, height: 0.8 } },
    { id: "plant-2", itemType: "plant", x: 28, y: 1, rotation: 0, collisionFootprint: { offsetX: 0.1, offsetY: 0.1, width: 0.8, height: 0.8 } },
    { id: "plant-3", itemType: "plant", x: 1, y: 22, rotation: 0, collisionFootprint: { offsetX: 0.1, offsetY: 0.1, width: 0.8, height: 0.8 } },
    { id: "plant-4", itemType: "plant", x: 28, y: 22, rotation: 0, collisionFootprint: { offsetX: 0.1, offsetY: 0.1, width: 0.8, height: 0.8 } },
  ],
  rooms: [
    {
      id: "focus-a", name: "Focus Room A", capacityLimit: 4,
      cells: Array.from({ length: 4 }, (_, y) => Array.from({ length: 5 }, (_, x) => ({ x: x + 3, y: y + 3 }))).flat()
    },
    {
      id: "focus-b", name: "Focus Room B", capacityLimit: 4,
      cells: Array.from({ length: 4 }, (_, y) => Array.from({ length: 5 }, (_, x) => ({ x: x + 11, y: y + 3 }))).flat()
    },
    {
      id: "meeting", name: "Meeting Room", capacityLimit: 10,
      cells: Array.from({ length: 4 }, (_, y) => Array.from({ length: 7 }, (_, x) => ({ x: x + 19, y: y + 3 }))).flat()
    },
    {
      id: "open-workspace", name: "Open Workspace", capacityLimit: 30,
      cells: Array.from({ length: 8 }, (_, y) => Array.from({ length: 28 }, (_, x) => ({ x: x + 1, y: y + 8 }))).flat()
    },
    {
      id: "lounge", name: "Lounge", capacityLimit: 12,
      cells: Array.from({ length: 5 }, (_, y) => Array.from({ length: 13 }, (_, x) => ({ x: x + 3, y: y + 17 }))).flat()
    },
    {
      id: "kitchen", name: "Kitchen", capacityLimit: 8,
      cells: Array.from({ length: 5 }, (_, y) => Array.from({ length: 8 }, (_, x) => ({ x: x + 19, y: y + 17 }))).flat()
    },
  ],
  spawns: [
    { id: "spawn-default", name: "Main Lobby", x: 15, y: 12 },
  ]
};
